import { Box } from '@mui/joy';
import { startCase } from 'lodash';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Confirmation } from 'core/components';
import { QuoteNoteType } from 'quotes/enums';
import { IBaseQuote } from 'quotes/types';
import { getQuoteNoteContent } from 'quotes/utils';

import { QuoteNoteTypeToCopyFieldMap } from './constants';
import { CopyNoteType, ItemsToCopyField } from './enums';

interface IProps {
  itemsToCopyValues: ItemsToCopyField[];
  sourceQuote: IBaseQuote;
  targetQuote: IBaseQuote;
  noteType: QuoteNoteType;
  copyNoteValue: string;
  isOpen: boolean;
  onConfirm: (noteType: QuoteNoteType, copyNoteType: CopyNoteType) => void;
  onClose: (noteType: QuoteNoteType) => void;
}

export const CopyNoteWarning = ({
  itemsToCopyValues,
  sourceQuote,
  targetQuote,
  noteType,
  copyNoteValue,
  isOpen,
  onConfirm,
  onClose,
}: IProps) => {
  const [warningMessage, setWarningMessage] = useState<JSX.Element | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const itemToCopy = QuoteNoteTypeToCopyFieldMap[noteType];

    if (!targetQuote?.id || !itemsToCopyValues.includes(itemToCopy) || !copyNoteValue) {
      setWarningMessage(null);

      return;
    }

    const sourceQuoteNoteContent = getQuoteNoteContent(sourceQuote, noteType);
    const targetQuoteNoteContent = getQuoteNoteContent(targetQuote, noteType);

    if (!targetQuoteNoteContent || !sourceQuoteNoteContent) {
      setWarningMessage(null);

      return;
    }

    setWarningMessage(
      <Trans
        i18nKey="messageCopyQuoteNoteWarning"
        values={{
          noteType: startCase(noteType),
          sourceNote: sourceQuoteNoteContent,
          targetNote: targetQuoteNoteContent,
        }}
        components={{
          wrapper: <Box></Box>,
          noteWrapper: <Box fontWeight={600}></Box>,
        }}
      />
    );
  }, [copyNoteValue, itemsToCopyValues, noteType, sourceQuote, targetQuote]);

  if (!warningMessage) {
    return null;
  }

  return (
    <Confirmation
      isOpen={isOpen}
      title={t('titleQuoteNoteAlreadyExists', { noteType })}
      message={warningMessage}
      confirmationActions={[
        {
          label: t('labelOverrideNote'),
          onClick: () => onConfirm(noteType, CopyNoteType.OVERRIDE),
          type: CopyNoteType.OVERRIDE,
        },
        {
          label: t('labelMergeNotes'),
          onClick: () => onConfirm(noteType, CopyNoteType.MERGE),
          type: CopyNoteType.MERGE,
        },
      ]}
      onCancel={() => onClose(noteType)}
    />
  );
};
